import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      // Create a unique key for items with variations
      const itemKey = action.payload.variation 
        ? `${action.payload.id}-${action.payload.variation.combination}`
        : action.payload.id;
      
      const existingItem = state.items.find(item => {
        if (action.payload.variation) {
          return item.id === action.payload.id && 
                 item.variation && 
                 item.variation.combination === action.payload.variation.combination;
        } else {
          return item.id === action.payload.id && !item.variation;
        }
      });
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item => {
            if (action.payload.variation) {
              return (item.id === action.payload.id && 
                      item.variation && 
                      item.variation.combination === action.payload.variation.combination)
                ? { ...item, quantity: item.quantity + action.payload.quantity }
                : item;
            } else {
              return (item.id === action.payload.id && !item.variation)
                ? { ...item, quantity: item.quantity + action.payload.quantity }
                : item;
            }
          })
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }]
        };
      }

    case 'REMOVE_FROM_CART':
      // Create a unique identifier for the item to remove
      const itemToRemoveKey = action.payload.variation 
        ? `${action.payload.id}-${action.payload.variation.combination}`
        : action.payload.id;
      
      const filteredItems = state.items.filter(item => {
        // Create a unique identifier for the current item
        const currentItemKey = item.variation 
          ? `${item.id}-${item.variation.combination}`
          : item.id;
        
        return currentItemKey !== itemToRemoveKey;
      });
      
      return {
        ...state,
        items: filteredItems
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item => {
          if (action.payload.variation) {
            return (item.id === action.payload.id && 
                    item.variation && 
                    item.variation.combination === action.payload.variation.combination)
              ? { ...item, quantity: action.payload.quantity }
              : item;
          } else {
            return (item.id === action.payload.id && !item.variation)
            ? { ...item, quantity: action.payload.quantity }
              : item;
          }
        })
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    case 'SET_CART':
      return {
        ...state,
        items: action.payload
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  // Initialize state with localStorage data
  const getInitialCart = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          // Validate each item has required properties
          const validItems = parsedCart.filter(item => 
            item && 
            typeof item === 'object' && 
            item.id && 
            typeof item.quantity === 'number' && 
            item.quantity > 0
          );
          
          if (validItems.length !== parsedCart.length) {
            console.warn('Some cart items were invalid and have been removed');
          }
          
          console.log('Initializing cart with', validItems.length, 'valid items from localStorage');
          return { items: validItems };
        } else {
          console.error('Invalid cart data format - expected array, got:', typeof parsedCart);
          localStorage.removeItem('cart');
        }
      }
    } catch (error) {
      console.error('Error loading initial cart from localStorage:', error);
      localStorage.removeItem('cart');
    }
    return { items: [] };
  };

  const [state, dispatch] = useReducer(cartReducer, getInitialCart());
  const [showNotification, setShowNotification] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      console.log('Saving cart to localStorage:', state.items);
      localStorage.setItem('cart', JSON.stringify(state.items));
      console.log('Cart saved successfully');
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [state.items]);

  const addToCart = (product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const removeFromCart = (product) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: product });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      // Find the item to remove
      const itemToRemove = state.items.find(item => item.id === productId);
      if (itemToRemove) {
        removeFromCart(itemToRemove);
      }
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    showNotification
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 